<script setup lang="ts">
// ServerPagination.vue - Paginação server-side seguindo o padrão do DataTablePagination
import { Button } from 'dashboard/components-shadcn/new-york/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'dashboard/components-shadcn/new-york/ui/select'
import { computed } from 'vue'

interface PaginationMeta {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
}

interface DeliveryStats {
  total: number
  billable: number
  sent: number
  delivered: number
  read: number
  failed: number
  pending: number
}

interface Props {
  meta: PaginationMeta
  stats?: DeliveryStats
  isLoading?: boolean
  hideBillable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  hideBillable: false,
})

const emit = defineEmits<{
  pageChange: [page: number]
  pageSizeChange: [size: number]
}>()

// Computed helpers
const canGoPrevious = computed(() => props.meta.current_page > 1)
const canGoNext = computed(() => props.meta.current_page < props.meta.total_pages)

const startRecord = computed(() => {
  if (props.meta.total_count === 0) return 0
  return (props.meta.current_page - 1) * props.meta.per_page + 1
})

const endRecord = computed(() => {
  const end = props.meta.current_page * props.meta.per_page
  return Math.min(end, props.meta.total_count)
})

// Handlers
const goToFirstPage = () => emit('pageChange', 1)
const goToPreviousPage = () => emit('pageChange', props.meta.current_page - 1)
const goToNextPage = () => emit('pageChange', props.meta.current_page + 1)
const goToLastPage = () => emit('pageChange', props.meta.total_pages)

const updatePageSize = (value: string) => {
  emit('pageSizeChange', parseInt(value))
}
</script>

<template>
  <div class="pagination-wrapper">
    <!-- Controles de paginação -->
    <div class="flex items-center justify-between px-2 pagination-container">
      <!-- Informação de registros -->
      <div class="flex-1 text-sm text-n-slate-10">
        <span v-if="meta.total_count > 0">
          Mostrando {{ startRecord }} - {{ endRecord }} de {{ meta.total_count.toLocaleString() }} registros
        </span>
        <span v-else>
          Nenhum registro encontrado
        </span>
      </div>

      <!-- Controles de paginação -->
      <div class="flex items-center space-x-6 lg:space-x-8 pagination-controls">
        <!-- Seletor de tamanho da página -->
        <div class="flex items-center space-x-2">
          <span class="text-sm font-medium text-n-slate-12 whitespace-nowrap leading-8">
            Linhas por página
          </span>
          <Select
            :model-value="`${meta.per_page}`"
            @update:model-value="updatePageSize"
          >
            <SelectTrigger class="h-8 w-[70px] bg-n-slate-4 border-n-slate-6 text-n-slate-12">
              <SelectValue :placeholder="`${meta.per_page}`" />
            </SelectTrigger>
            <SelectContent side="top" class="bg-n-slate-1 border-n-slate-6">
              <SelectItem v-for="pageSize in [10, 25, 50, 100]" :key="pageSize" :value="`${pageSize}`">
                {{ pageSize }}
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <!-- Informação da página atual -->
        <div class="flex items-center justify-center text-sm font-medium text-n-slate-12 page-info whitespace-nowrap px-3">
          Página {{ meta.current_page }} de {{ meta.total_pages || 1 }}
        </div>

        <!-- Botões de navegação -->
        <div class="flex items-center space-x-2">
          <!-- Primeira página -->
          <Button
            variant="outline"
            class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!canGoPrevious || isLoading"
            @click="goToFirstPage"
            title="Primeira página"
          >
            <span class="sr-only">Ir para primeira página</span>
            <span class="i-lucide-chevrons-left h-4 w-4 text-n-slate-11"></span>
          </Button>

          <!-- Página anterior -->
          <Button
            variant="outline"
            class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!canGoPrevious || isLoading"
            @click="goToPreviousPage"
            title="Página anterior"
          >
            <span class="sr-only">Ir para página anterior</span>
            <span class="i-lucide-chevron-left h-4 w-4 text-n-slate-11"></span>
          </Button>

          <!-- Próxima página -->
          <Button
            variant="outline"
            class="h-8 w-8 p-0 border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!canGoNext || isLoading"
            @click="goToNextPage"
            title="Próxima página"
          >
            <span class="sr-only">Ir para próxima página</span>
            <span class="i-lucide-chevron-right h-4 w-4 text-n-slate-11"></span>
          </Button>

          <!-- Última página -->
          <Button
            variant="outline"
            class="hidden h-8 w-8 p-0 lg:flex border-n-slate-6 hover:bg-n-slate-3"
            :disabled="!canGoNext || isLoading"
            @click="goToLastPage"
            title="Última página"
          >
            <span class="sr-only">Ir para última página</span>
            <span class="i-lucide-chevrons-right h-4 w-4 text-n-slate-11"></span>
          </Button>
        </div>
      </div>
    </div>

    <!-- Barra de status com estatísticas -->
    <div
      v-if="stats && stats.total > 0"
      class="flex items-center justify-between text-xs text-n-slate-10 px-4 py-3 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-100 dark:border-green-800 mt-4"
    >
      <div class="flex items-center gap-4 flex-wrap">
        <!-- Total -->
        <div class="flex items-center gap-1">
          <span class="i-lucide-mail w-3 h-3"></span>
          <span>{{ stats.total.toLocaleString() }} total</span>
        </div>

        <!-- Enviadas -->
        <div class="flex items-center gap-1 text-amber-600 dark:text-amber-400">
          <span class="i-lucide-send w-3 h-3"></span>
          <span>{{ stats.sent.toLocaleString() }} enviadas</span>
        </div>

        <!-- Entregues -->
        <div class="flex items-center gap-1 text-slate-600 dark:text-slate-400">
          <span class="i-lucide-check w-3 h-3"></span>
          <span>{{ stats.delivered.toLocaleString() }} entregues</span>
        </div>

        <!-- Lidas -->
        <div class="flex items-center gap-1 text-blue-600 dark:text-blue-400">
          <span class="i-lucide-check-check w-3 h-3"></span>
          <span>{{ stats.read.toLocaleString() }} lidas</span>
        </div>

        <!-- Falhas -->
        <div v-if="stats.failed > 0" class="flex items-center gap-1 text-red-600 dark:text-red-400">
          <span class="i-lucide-x-circle w-3 h-3"></span>
          <span>{{ stats.failed.toLocaleString() }} falhas</span>
        </div>

        <!-- Faturáveis (oculto para Baileys) -->
        <div v-if="!hideBillable" class="flex items-center gap-1 text-green-600 dark:text-green-400">
          <span class="i-lucide-receipt w-3 h-3"></span>
          <span>{{ stats.billable.toLocaleString() }} faturáveis</span>
        </div>
      </div>

      <!-- Indicador de carregamento -->
      <div v-if="isLoading" class="flex items-center gap-2">
        <div class="w-3 h-3 border border-green-500 border-t-transparent rounded-full animate-spin"></div>
        <span class="text-green-600 dark:text-green-400">Carregando...</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Container de paginação seguindo o padrão WhatsApp */
.pagination-wrapper {
  @apply border border-slate-200 dark:border-slate-800 p-2 mb-2 rounded-md;
}

/* Pagination Styling seguindo o padrão */
:deep(.pagination-container) {
  @apply text-n-slate-10;
}

/* Garantir que as variáveis CSS do sistema funcionem no select */
:deep([data-radix-select-trigger]) {
  @apply bg-n-slate-1 border-n-slate-6 text-n-slate-12;
}

:deep([data-radix-select-trigger]:focus) {
  @apply ring-2 ring-green-500 border-green-500;
}

:deep([data-radix-select-content]) {
  @apply bg-n-slate-1 border-n-slate-6 shadow-lg rounded-lg p-1;
}

:deep([data-radix-select-item]) {
  @apply text-n-slate-12 bg-n-slate-1 cursor-pointer rounded px-3 py-2 mx-0.5;
}

:deep([data-radix-select-item]:hover) {
  @apply bg-n-slate-3;
}

:deep([data-radix-select-item][data-highlighted]) {
  @apply bg-n-slate-3 text-n-slate-12;
}

/* Estados disabled */
:deep(button:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Hover effects para botões seguindo o padrão WhatsApp */
:deep(button:not(:disabled):hover) {
  transition: all 0.2s ease;
}

/* Botões de paginação com cores originais */
:deep(.pagination-controls button) {
  @apply border-n-slate-6 hover:bg-n-slate-3;
}

/* Responsividade */
@media (max-width: 768px) {
  .pagination-container {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .pagination-controls {
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .page-info {
    width: auto;
    min-width: 120px;
  }
}
</style>
